import React from "react";
import { MontserratText, NoticiaText } from "@/components/Typography";
import { Phone } from "lucide-react";
import { motion } from "framer-motion";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";
import Link from "next/link";

interface LogoProps {
  phone: string;
  locale: string;
  ccn?: string;
  line1?: string;
  line2?: string;
  subtext?: string;
  subtextClassName?: string;
}

export const Logo = ({
  phone,
  locale,
  ccn,
  line1,
  line2,
  subtext,
  subtextClassName,
}: LogoProps) => {
  const defaultCcn = "";
  const defaultLine1 = "";
  const defaultLine2 = "";
  const defaultSubtext = "";

  const displayCcn = ccn || defaultCcn;
  const displayLine1 = line1 || defaultLine1;
  const displayLine2 = line2 || defaultLine2;
  const displaySubtext = subtext || defaultSubtext;

  return (
    <div className="flex flex-col items-center select-none relative">
      {/* Top row containing logo mark and titles */}
      <Link
        href={`/${locale}`}
        className="flex items-center gap-1.5 sm:gap-2 md:gap-3 hover:opacity-90 transition-opacity cursor-pointer"
      >
        {/* Part 1: Logo Image */}
        <div className="flex-shrink-0 flex items-center">
          <img
            src="/logos/ccn_logo.png"
            alt="ccn tay bac duc hoa dong Logo"
            className="h-[24px] w-auto sm:h-[34px] md:h-[48px] lg:h-[62px] object-contain brightness-0 invert"
          />
        </div>

        {/* Part 2: CCN vertical column */}
        <div className="flex flex-col justify-center items-center leading-[0.8] sm:leading-[0.85] md:leading-[0.9]">
          {displayCcn.split("").map((char, index) => (
            <MontserratText
              key={index}
              as="span"
              mobile="8px"
              mobileLg="10px"
              sm="10px"
              md="15px"
              lg="19px"
              className="text-white font-bold leading-none select-none text-center"
            >
              {char}
            </MontserratText>
          ))}
        </div>

        {/* Part 3: Main Text column */}
        <div className="flex flex-col text-left justify-center">
          <MontserratText
            as="span"
            mobile="11px"
            mobileLg="15px"
            sm="15px"
            md="22px"
            lg="28px"
            className="font-bold"
            style={{
              background: "linear-gradient(to right, #DBB653, #FFF0B8)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              verticalAlign: "bottom",
            }}
          >
            {displayLine1}
          </MontserratText>
          <MontserratText
            as="span"
            mobile="11px"
            mobileLg="15px"
            sm="15px"
            md="22px"
            lg="28px"
            variant="logoTitle"
            className="font-bold -mt-[2px] sm:-mt-[5px] md:-mt-[8px] lg:-mt-[10px]"
            style={{
              background: "linear-gradient(to right, #DBB653, #FFF0B8)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              verticalAlign: "top",
            }}
          >
            {displayLine2}
          </MontserratText>
        </div>
      </Link>

      {/* Official developer info subtext line below the main elements - positioned absolutely to prevent layout expansion */}
      <div className="absolute top-full left-1/2 -translate-x-1/2 w-max pointer-events-none flex flex-col items-center gap-y-0.5 sm:gap-y-1">
        <MontserratText
          as="div"
          variant="logoSub"
          sizes={{
            mobile: "10px",
            mobileLg: "10px",
            tablet: "18px",
            laptop: "15px",
            desktop: "20px",
          }}
          className={`text-white font-medium tracking-[0.05em] sm:tracking-[0.08em] md:tracking-[0.12em] lg:tracking-[0.15em] text-center uppercase whitespace-nowrap ${subtextClassName || ""}`}
        >
          {displaySubtext}
        </MontserratText>
        <a
          href="tel:090202020022"
          className="pointer-events-auto cursor-pointer flex items-center justify-center gap-x-2 sm:gap-x-3 md:gap-x-4 hover:underline hover:decoration-white select-none mt-1 sm:mt-1.5"
        >
          {/* Animated Circle Wrapper with Bling/Ripple Effects */}
          <div className="relative flex items-center justify-center w-5 h-5 sm:w-7 sm:h-7 md:w-9 md:h-9 lg:w-11 lg:h-11 flex-shrink-0">
            {/* Bling ripple ring 1 */}
            <motion.div
              className="absolute inset-0 rounded-full bg-[#DBB653]/35"
              animate={{ scale: [1, 1.8], opacity: [0.8, 0] }}
              transition={{ repeat: Infinity, duration: 1.6, ease: "easeOut" }}
            />
            {/* Bling ripple ring 2 */}
            <motion.div
              className="absolute inset-0 rounded-full border border-[#DBB653]/60"
              animate={{ scale: [1, 2.3], opacity: [0.6, 0] }}
              transition={{
                repeat: Infinity,
                duration: 1.6,
                delay: 0.4,
                ease: "easeOut",
              }}
            />
            {/* The Central Circle carrying the ringing Phone Icon */}
            <motion.span
              animate={{
                rotate: [0, -15, 15, -15, 15, 0],
              }}
              transition={{
                duration: 1.2,
                repeat: Infinity,
                repeatDelay: 2,
                ease: "easeInOut",
              }}
              className="relative z-10 w-full h-full rounded-full bg-gradient-to-br from-[#DBB653] to-[#FFF0B8] flex items-center justify-center shadow-[0_0_10px_rgba(219,182,83,0.4)]"
            >
              <Phone className="w-[50%] h-[50%] text-[#0b3848] fill-current" />
            </motion.span>
          </div>

          <MontserratText
            as="span"
            variant="logoSub"
            sizes={{
              mobile: "15px",
              mobileLg: "16px",
              tablet: "26px",
              laptop: "22px",
              desktop: "28px",
            }}
            className={`text-[#DBB653] font-bold tracking-[0.05em] sm:tracking-[0.08em] md:tracking-[0.12em] lg:tracking-[0.15em] text-center uppercase whitespace-nowrap ${subtextClassName || ""}`}
          >
            {formatPhoneNumber(phone)}
          </MontserratText>
        </a>
      </div>
    </div>
  );
};
